import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGuard } from '@/features/auth';
import { useAddOnGroupQuery } from '../hooks/useAddOnGroupQuery';
import { useAddOnGroupMutations } from '../hooks/useAddOnGroupMutations';
import { Alert, Button, ConfirmActionModal, ButtonLink, TextLink } from '@shared/components';

export function AddOnGroupDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const query = useAddOnGroupQuery(id);
    const { retireMutation, deleteMutation } = useAddOnGroupMutations();
    const [retireOpen, setRetireOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    if (!id) return <p className="text-mp-error">Invalid group ID.</p>;
    if (query.isLoading) return <p>Loading group...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Group not found or failed to load.</span></Alert>;
    }

    const group = query.data;

    const handleRetire = () => setRetireOpen(true);

    const handleConfirmRetire = () => {
        retireMutation.mutate(id);
        setRetireOpen(false);
    };

    const handleDelete = () => setDeleteOpen(true);

    const handleConfirmDelete = () => {
        deleteMutation.mutate(id, {
            onSuccess: () => navigate('/smr-admin/addon-groups'),
        });
        setDeleteOpen(false);
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to="/smr-admin/addon-groups" color="secondary" className="text-sm">&larr; Back to Optional Extra Groups</TextLink>
            </div>

            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">{group.name}</h1>
                <div className="flex gap-2">
                    <PermissionGuard permission={{ resource: 'smr', action: 'addon-group.edit' }}>
                        <ButtonLink to={`/smr-admin/addon-groups/${id}/edit`} variant="outline" size="xs">Edit</ButtonLink>
                    </PermissionGuard>
                    {!group.retired_at && (
                        <PermissionGuard permission={{ resource: 'smr', action: 'addon-group.delete' }}>
                            <Button
                                type="button"
                                variant="warning"
                                appearance="solid"
                                size="xs"
                                onClick={handleRetire}
                                disabled={retireMutation.isPending}
                            >
                                Retire
                            </Button>
                        </PermissionGuard>
                    )}
                    <PermissionGuard permission={{ resource: 'smr', action: 'addon-group.delete' }}>
                        <Button
                            type="button"
                            variant="danger"
                            appearance="solid"
                            size="xs"
                            onClick={handleDelete}
                            disabled={deleteMutation.isPending}
                        >
                            Delete
                        </Button>
                    </PermissionGuard>
                </div>
            </div>

            {deleteMutation.isError && (
                <Alert variant="error">
                    <span>{(deleteMutation.error as Error)?.message ?? 'Delete failed.'}</span>
                </Alert>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <p className="text-sm text-mp-muted">Status</p>
                    <p className="font-medium">{group.retired_at ? 'Retired' : 'Active'}</p>
                </div>
                {group.description && (
                    <div className="md:col-span-2">
                        <p className="text-sm text-mp-muted">Description</p>
                        <p className="font-medium">{group.description}</p>
                    </div>
                )}
            </div>

            {group.addons && group.addons.length > 0 && (
                <div>
                    <p className="text-sm text-mp-muted mb-2">Member Optional Extras ({group.addons.length})</p>
                    <ul className="space-y-1">
                        {group.addons.map((addon) => (
                            <li key={addon.id} className="flex items-center gap-2 text-sm">
                                <span className="font-medium">{addon.name}</span>
                                {addon.category && <span className="text-mp-muted">({addon.category})</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div data-testid="addon-group-assigned-products-placeholder" className="mt-6 rounded border border-dashed border-mp-border p-4 text-sm text-mp-muted">
                <p className="font-medium mb-1">Assigned products</p>
                <p>Products that have this optional extra group assigned will be listed here once the product↔group assignment surface ships (Epic 7.9 PR2).</p>
            </div>

            <ConfirmActionModal
                isOpen={retireOpen}
                title="Retire optional extra group"
                message="Retire this group? Existing product assignments are preserved."
                confirmLabel="Retire"
                confirmVariant="warning"
                isPending={retireMutation.isPending}
                onConfirm={handleConfirmRetire}
                onClose={() => setRetireOpen(false)}
            />

            <ConfirmActionModal
                isOpen={deleteOpen}
                title="Delete optional extra group"
                message="Delete this group permanently? This will fail if the group is assigned to any product."
                confirmLabel="Delete"
                confirmVariant="danger"
                isPending={deleteMutation.isPending}
                onConfirm={handleConfirmDelete}
                onClose={() => setDeleteOpen(false)}
            />
        </section>
    );
}
